import { AI_MARKERS, type AiMarkerKind } from "./markers";

export type ParsedAiAlt = {
  cleanAlt: string;
  isAi: boolean;
  kind: AiMarkerKind | null;
};

/**
 * Trailing whitespace plus the invisible characters `String.prototype.trim()` leaves
 * behind. NBSP and BOM are already in ECMAScript's WhiteSpace production and so are
 * covered by `\s`; the rest are not. Soft hyphen (U+00AD) is the one that matters in
 * practice — it is what Word and Google Docs inject on paste, and alt text reaches us
 * from CMS editors.
 */
const NOISE_CHAR = /^(?:\s|\u200B|\u200C|\u200D|\u00AD|\u2060|\uFEFF)$/;

function isNoise(char: string | undefined): boolean {
  return char !== undefined && NOISE_CHAR.test(char);
}

/**
 * Scans backwards instead of using an anchored `(...)+$` regex: the engine tries that
 * pattern at every offset, so a long run of whitespace followed by a non-noise
 * character costs O(n²) — a ReDoS on alt text that comes straight from editors.
 */
function stripTrailingNoise(value: string): string {
  let end = value.length;
  while (end > 0 && isNoise(value[end - 1])) end--;
  return value.slice(0, end);
}

function findTrailingMarker(value: string) {
  const lower = value.toLowerCase();
  return AI_MARKERS.find((entry) => lower.endsWith(entry.marker));
}

/**
 * Removes the single separator `applyAiMarker` inserts before a marker — no more.
 *
 * Stripping every trailing character here instead would make the round trip lossy:
 * `applyAiMarker("hello ")` yields `"hello  ::ai"`, and a greedy strip would give back
 * `"hello"`, silently eating a space the editor typed.
 */
function stripOneSeparator(value: string): string {
  return isNoise(value[value.length - 1]) ? value.slice(0, -1) : value;
}

function markerFor(kind: AiMarkerKind): string {
  const entry = AI_MARKERS.find((candidate) => candidate.kind === kind);
  if (!entry) {
    throw new Error(`Unknown AI marker kind: ${kind}`);
  }
  return entry.marker;
}

/**
 * Splits an alt string into the text a reader should see and the AI marker it carried.
 *
 * Markers are matched only at the end of the string, so alt text that legitimately
 * discusses AI is left alone. Stripping repeats until no marker remains: a single pass
 * would leave `"foo::ai-modified::ai-generated"` still ending in a marker, and since
 * `removeAiMarker` is defined in terms of `cleanAlt`, `applyAiMarker` would then append
 * a third one. The loop terminates because every iteration removes at least a marker's
 * worth of characters.
 *
 * `kind` reports the outermost marker — the last one applied.
 *
 * Unmarked text is returned byte-for-byte. Normalizing it would corrupt content at
 * scale: the control page runs this over every asset, most of which carry no marker,
 * and an editor's leading or trailing whitespace is theirs to keep.
 */
export function parseAiAlt(alt: string | null | undefined): ParsedAiAlt {
  if (typeof alt !== "string") {
    return { cleanAlt: "", isAi: false, kind: null };
  }

  let rest = alt;
  let kind: AiMarkerKind | null = null;

  for (;;) {
    // Noise is stripped to find the marker, never to rewrite the caller's text.
    const probe = stripTrailingNoise(rest);
    const entry = findTrailingMarker(probe);
    if (!entry) break;

    kind ??= entry.kind;
    rest = probe.slice(0, probe.length - entry.marker.length);
  }

  if (kind === null) {
    return { cleanAlt: alt, isAi: false, kind: null };
  }

  return { cleanAlt: stripOneSeparator(rest), isAi: true, kind };
}

/** Strips every trailing AI marker, returning the text a reader should see. */
export function removeAiMarker(alt: string): string {
  return parseAiAlt(alt).cleanAlt;
}

/**
 * Marks alt text as AI-generated, replacing any marker already present.
 *
 * Removing first is what makes this idempotent and safe to re-run over content that is
 * already marked — including a kind swap, where an editor changes their mind. The
 * control page applies this in bulk, so leaving a stale marker behind would corrupt
 * alt text at scale.
 */
export function applyAiMarker(
  alt: string,
  kind: AiMarkerKind = "unspecified",
): string {
  const base = removeAiMarker(alt);
  const marker = markerFor(kind);
  return base ? `${base} ${marker}` : marker;
}
